import { candleTime, composeReferenceCapture, deadline, openReferenceChart, REFERENCE_CHART_PAIRS } from "./referenceChartWidget";

const CAPTURE_TIMEOUT_MS = 120000;

/** Capture the setup at the base candle's close, including the as-of developing HTF bar.
 * `time` is the base candle OPEN in Unix seconds; `container` must be mounted,
 * measurable and offscreen (never display:none). No original chart is changed.
 */
export async function captureReferencePair({ charts, symbol, time, slot, signal, container }) {
  const pair = REFERENCE_CHART_PAIRS[charts?.[0]?.spec?.label];
  if (!pair || charts.length !== 2 || !charts.every((chart, index) => chart.spec.resolution === pair[index].resolution
      && chart.spec.seconds === pair[index].seconds)) throw new Error("The reference chart pair is unavailable.");
  const decisionSeconds = time + pair[0].seconds;
  if (!Number.isSafeInteger(time) || time < 0 || candleTime(time, pair[0]) !== time
      || !Number.isSafeInteger(decisionSeconds * 1000) || decisionSeconds * 1000 > Date.now()) {
    throw new Error("Choose a base candle that has already closed before capturing its setup.");
  }
  if (!container?.isConnected || container.getBoundingClientRect().width <= 0) {
    throw new Error("The setup chart capture area is unavailable.");
  }
  const controller = new AbortController();
  const temporaryCharts = [];
  const hosts = [];
  const ranges = charts.map((chart) => chart.chart.getVisibleRange());
  const rangeKeys = ranges.map((range) => JSON.stringify(range));
  const drawings = charts.map((chart) => chart.exportUserDrawings(decisionSeconds));
  const decisionTime = new Date(decisionSeconds * 1000).toISOString();
  const asOf = decisionTime.replace(".000Z", " UTC");
  const higherDeveloping = candleTime(decisionSeconds, pair[1]) !== decisionSeconds;
  let timedOut = false;
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) abort();
  const timer = setTimeout(() => { timedOut = true; abort(); }, CAPTURE_TIMEOUT_MS);
  const check = () => {
    if (controller.signal.aborted) throw new Error(timedOut ? "Reference capture timed out. Please try again." : "Reference capture cancelled.");
  };
  // A failed sibling immediately cancels all temporary feeds and widgets, then
  // waits for every opening/layout operation to settle before removing hosts.
  const together = async (jobs) => {
    let failure;
    const settled = await Promise.allSettled(jobs.map((job) => job.catch((error) => {
      if (!failure) failure = error;
      abort();
      throw error;
    })));
    if (failure) throw failure;
    return settled.map((result) => result.value);
  };
  try {
    check();
    const outcomeDataUrl = await composeReferenceCapture(charts, symbol, time, slot, {
      signal: controller.signal,
      header: `OUTCOME · review-only · selected open ${new Date(time * 1000).toISOString().replace(".000Z", " UTC")}`,
    });
    check();
    await together(pair.map(async (spec, index) => {
      check();
      const host = document.createElement("div");
      const chartWidth = Math.floor(charts[index].getWidth());
      if (!Number.isFinite(chartWidth) || chartWidth <= 0) throw new Error("The original chart dimensions are unavailable.");
      host.style.width = `${chartWidth}px`;
      host.style.height = "540px";
      container.appendChild(host);
      hosts.push(host);
      const chart = await openReferenceChart(host, symbol, spec, controller.signal, null, null, {
        cutoffTime: decisionSeconds,
        partialBaseResolution: index === 1 && spec.seconds > pair[0].seconds ? pair[0].resolution : undefined,
      });
      temporaryCharts.push(chart);
      return chart;
    })).then((ordered) => { temporaryCharts.splice(0, temporaryCharts.length, ...ordered); });
    check();
    const sampleTimes = pair.map((spec) => candleTime(decisionSeconds - 1, spec));
    await together(temporaryCharts.map(async (chart, index) => {
      check();
      const originalSpan = ranges[index]?.to - ranges[index]?.from;
      const span = Number.isFinite(originalSpan) && originalSpan >= pair[index].seconds * 2
        && originalSpan <= pair[index].seconds * 5000 ? originalSpan : pair[index].seconds * 120;
      const sample = sampleTimes[index];
      await deadline(chart.chart.setVisibleRange({
        from: Math.max(0, sample - span * 0.9), to: sample + pair[index].seconds,
      }, { percentRightMargin: 10 }), "Could not position the setup chart.", 30000, controller.signal);
      await chart.ready();
      check();
      if (!chart.hasCandle(sample)) throw new Error("The required as-of candle is unavailable for this setup.");
      chart.importUserDrawings(drawings[index]);
      await chart.mark(sample, index === 0 || pair[1].seconds === pair[0].seconds ? "Selected base candle"
        : higherDeveloping ? `Developing ${pair[1].label} candle` : "Last completed higher candle");
    }));
    check();
    const dataUrl = await composeReferenceCapture(temporaryCharts, symbol, time, slot, {
      signal: controller.signal, sampleTimes,
      headers: [
        `SETUP · as-of close ${asOf} · closed base candles`,
        `SETUP · as of ${asOf} · ${higherDeveloping ? "last candle developing" : "closed candles only"}`,
      ],
    });
    check();
    if (charts.some((chart, index) => JSON.stringify(chart.chart.getVisibleRange()) !== rangeKeys[index])) {
      throw new Error("An outcome chart moved during capture. Wait for it to settle and capture again.");
    }
    return { dataUrl, outcomeDataUrl, decisionTime, capturePolicy: "as-of-htf-v2" };
  } catch (error) {
    if (timedOut) throw new Error("Reference capture timed out. Please try again.");
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
    abort();
    for (const chart of temporaryCharts) chart.dispose();
    for (const host of hosts) host.remove();
  }
}
